import React, { useCallback } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
    CheckboxField, InputField, ToggleField, InputNumberField, DropdownSelectField
} from '@jutro/components';
import Styles from './QuestionParserComponent.module.scss';

const toggleAvailableValues = [
    {
        code: 'true',
        name: {
            id: 'platform.inputs.Yes',
            defaultMessage: 'Yes'
        }
    },
    {
        code: 'false',
        name: {
            id: 'platform.inputs.No',
            defaultMessage: 'No'
        }
    }
];

function QuestionParserComponent({ questionSet, handleValueChange, stateList }) {
    const resetCheckboxSelectedValues = useCallback((value, optionPath) => {
        const questionList = [...questionSet];
        const path = _.split(optionPath, '.', 1)[0];
        let options = [];
        if (value) {
            if (_.get(questionList, `${optionPath}.code`) === 'None') {
                options = _.get(questionList, path).options.map((item) => {
                    return _.set(item, 'value', item.code !== 'None' ? false : value);
                });
                _.set(questionList, `${path}.options`, options);
            } else {
                const resetIndex = _.findIndex(_.get(questionList, path).options, { code: 'None' });
                _.set(questionList, `${path}.options[${resetIndex}].value`, false);
                _.set(questionList, `${optionPath}.value`, value);
            }
            return handleValueChange(value, optionPath, questionList);
        }
        if (!value && _.get(questionSet, `${optionPath}.hasValue`)) {
            const code = _.get(questionSet, `${optionPath}.code`);
            options = _.get(questionList, path).options.map((item) => {
                return _.set(item, 'value', item.visible === code ? '' : _.get(item, 'value'));
            });
            _.set(questionList, `${path}.options`, options);
        }
        return handleValueChange(value, optionPath);
    }, [handleValueChange, questionSet]);

    const resetRadioValuesSwitched = useCallback((value, optionPath) => {
        const questionList = [...questionSet];
        const path = _.split(optionPath, '.', 1)[0];
        const questionOptions = _.get(questionList, path).options;
        if (!value && !_.isEmpty(questionOptions)) {
            const options = questionOptions.map((item) => {
                return _.set(item, 'value', _.isBoolean(item, 'value') ? false : null);
            });
            _.set(questionList, `${path}.options`, options);
        }
        const rootQuestionCode = _.get(questionList, `${path}.code`);
        if (!_.isEmpty(rootQuestionCode)) {
            const questions = questionList.map((item) => {
                return item.visible === rootQuestionCode && _.set(item, 'value', null);
            });
            _.set(questionList, '', questions);
        }
        return handleValueChange(value, optionPath);
    }, [handleValueChange, questionSet]);

    const onValueChange = useCallback((value, optionPath) => {
        if (_.get(questionSet, `${optionPath}.type`) === 'CheckboxField') {
            return resetCheckboxSelectedValues(value, optionPath);
        }
        if (_.get(questionSet, `${optionPath}.type`) === 'radio') {
            return resetRadioValuesSwitched(value, optionPath);
        }
        return handleValueChange(value, optionPath);
    }, [handleValueChange, questionSet, resetCheckboxSelectedValues, resetRadioValuesSwitched]);
    const generateCheckBox = useCallback((option, index, optionPath) => {
        if (_.get(questionSet, `${optionPath}.type`) === 'radio' && (!_.get(questionSet, `${optionPath}.value`)
        || _.isNull(questionSet, `${optionPath}.value`))) {
            return null;
        }
        const path = `${optionPath}.options[${index}]`;
        return (
            <CheckboxField
                id={`${option.label}${index}`}
                value={option.value}
                label={option.label}
                path={path}
                size="small"
                className={Styles.questionPadding}
                showInlineLabel={option.showInlineLabel || true}
                onValueChange={onValueChange}
            />
        );
    }, [onValueChange, questionSet]);
    const generateInputFieldboxField = useCallback((option, index, optionPath, options) => {
        if (_.get(questionSet, `${optionPath}.type`) === 'radio' && (!_.get(questionSet, `${optionPath}.value`)
        || _.isNull(questionSet, `${optionPath}.value`))) {
            return null;
        }
        if (typeof option.visible === 'string') {
            const subOptionValid = _.some(_.get(questionSet, `${optionPath}.options`),
                (item) => item.type === 'CheckboxField' && item.code === _.get(options[index], 'visible') && (item.value));
            if (!subOptionValid) { return null; }
        }
        const path = `${optionPath}.options[${index}]`;
        return (
            <InputField
                id={`${option.label}${index}`}
                value={option.value}
                label={option.label}
                path={path}
                size="small"
                className={cx(Styles.questionPadding, {
                    [Styles.textBoxSubStyle]: !_.isEmpty(option, 'code')
                })}
                maxLength={option.maxLength}
                hideLabel={option.hideLabel}
                required={_.get(_.find(options, { code: option.required }), 'value') === true || option.required === true}
                visible={_.get(_.find(options, { code: option.visible }), 'value') === true || option.visible === true}
                onValueChange={onValueChange}
            />
        );
    }, [onValueChange, questionSet]);
    const generateRadioButton = useCallback((option, index, optionPath) => {
        const path = option.label ? `${optionPath}.options[${index}]` : optionPath;
        if (_.has(option, 'isRootVisible') && option.isRootVisible) {
            const baseRootData = _.find(questionSet, { code: option.visible });
            if (baseRootData.value !== true) {
                return null;
            }
        }
        let labelOrder = index + 1;
        const questionSetList = _.take(questionSet, option.order - 1);
        const rootVisibleElements = _.filter(questionSetList, { isRootVisible: true });
        if (!_.isEmpty(rootVisibleElements)) {
            const previousNullValueList = _.filter(rootVisibleElements, (element) => {
                return !_.find(questionSet, { code: element.visible }).value;
            });
            labelOrder -= previousNullValueList.length;
        }
        return (
            <ToggleField
                id={`${option.label || option.question}${index}`}
                value={option.value}
                label={`${labelOrder}. ${option.label || option.question}`}
                path={path}
                size="small"
                labelPosition={option.labelPosition || 'left'}
                layout="reversed"
                className={Styles.questionPadding}
                availableValues={toggleAvailableValues}
                onValueChange={onValueChange}
            />
        );
    }, [onValueChange, questionSet]);
    const generateNumericField = useCallback((option, index, optionPath) => {
        const path = option.label ? `${optionPath}.options[${index}]` : optionPath;
        return (
            <InputNumberField
                type="field"
                id={`${option.label || option.question}${index}`}
                value={option.value}
                path={path}
                size="small"
                label={`${option.order}. ${option.label || option.question}`}
                onValueChange={onValueChange}
                className={cx(Styles.questionPadding, Styles.textFontStyle)}
                required={option.required}
                minValue={option.minValue || 0}
                maxValue={option.maxValue || 999}
            />
        );
    }, [onValueChange]);
    const generateDropdownList = useCallback((option, index, optionPath) => {
        if (_.get(questionSet, `${optionPath}.type`) === 'radio' && (!_.get(questionSet, `${optionPath}.value`)
        || _.isNull(questionSet, `${optionPath}.value`))) {
            return null;
        }
        const path = option.label ? `${optionPath}.options[${index}]` : optionPath;
        return (
            <DropdownSelectField
                type="field"
                id={`${option.label || option.question}${index}`}
                label={`${option.order ? `${option.order}.` : ''} ${option.label || option.question}`}
                value={option.value}
                path={path}
                size="small"
                layout="reversed"
                labelPosition={option.labelPosition || 'left'}
                availableValues={option.isState ? stateList : option.availableValues}
                onValueChange={handleValueChange}
                className={Styles.questionPadding}
            />
        );
    }, [handleValueChange, questionSet, stateList]);
    if (_.isEmpty(questionSet)) {
        return null;
    }
    return (
        <div>
            {
                questionSet.map((questionList, questionIndex) => (
                    <div className={Styles.surveyContainer}>
                        {_.get(questionList, 'type') === 'title' && <div className={Styles.textFontStyle}>{`${questionList.order}. ${questionList.question}`}</div>}
                        {_.get(questionList, 'type') === 'radio' && generateRadioButton(questionList, questionIndex, `[${questionIndex}]`)}
                        {_.get(questionList, 'type') === 'numeric' && generateNumericField(questionList, questionIndex, `[${questionIndex}]`)}
                        {_.get(questionList, 'type') === 'dropdown' && generateDropdownList(questionList, questionIndex, `[${questionIndex}]`)}
                        <div className={Styles.optionStyle}>
                            {questionList.options
                            && questionList.options.map((option, optionIndex) => {
                                const path = `[${questionIndex}]`;
                                if (option.type === 'CheckboxField') {
                                    return generateCheckBox(option, optionIndex, path);
                                }
                                if (option.type === 'input') {
                                    return generateInputFieldboxField(option, optionIndex,
                                        path, questionList.options);
                                }
                                if (option.type === 'radio') {
                                    return generateInputFieldboxField(option, optionIndex, path);
                                }
                                if (option.type === 'dropdown') {
                                    return generateDropdownList(option, optionIndex, path);
                                }
                                if (option.type === 'numeric') {
                                    return generateNumericField(option, optionIndex, path);
                                }
                                return null;
                            })}
                        </div>
                    </div>
                ))
            }
        </div>
    );
}

QuestionParserComponent.propTypes = {
    questionSet: PropTypes.shape([]).isRequired,
    handleValueChange: PropTypes.func.isRequired,
    stateList: PropTypes.shape([])
};

QuestionParserComponent.defaultProps = {
    stateList: []
};

export default QuestionParserComponent;
